import Axios, { AxiosResponse } from 'axios';
import { isError } from '../utils';
import {
  AbortErrorName,
  AbortReason,
  ApiErrorMessage,
  AuthorizationHeaderKey,
  BaseHeaders,
  HttpMethod,
  NetworkErrorMessage,
} from './constants';
import {
  ApiFailureResult,
  ApiRequestConfig,
  ApiResult,
  CreateApiHandlerConfig,
  GeneralApiResponse,
  GeneralApiResponseData,
  OnRefreshFail,
  RefreshTokenResponse,
} from './types';
import {
  createApiFailureResult,
  createApiSuccessResult,
  createRequestConfig,
} from './utils';

export function createApiHandler(createConfig: CreateApiHandlerConfig) {
  const { baseUrl, baseHeaders } = createConfig;

  const axios = Axios.create({
    baseURL: baseUrl,
    headers: baseHeaders ? baseHeaders : BaseHeaders,
  });

  const axiosForRefresh = Axios.create({
    baseURL: baseUrl,
    headers: baseHeaders ? baseHeaders : BaseHeaders,
  });

  let onRefreshFail: OnRefreshFail = null;
  let isRefreshing = false;
  let failedQueue: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolve: (value?: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (error?: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config: any;
  }[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      } else {
        if (token && createConfig.addAuthHeader) {
          config.headers[createConfig.authHeaderKey ?? AuthorizationHeaderKey] =
            token;
        }
        resolve(axios(config));
      }
    });

    failedQueue = [];
  };

  if (createConfig.addAuthHeader) {
    axios.interceptors.request.use(req => {
      if (req.withAuth) {
        const abortController = new AbortController();

        req.signal = abortController.signal;

        const authHeaderValue =
          req.accessToken ?? createConfig.getAuthHeaderValue?.() ?? '';

        if (authHeaderValue) {
          req.headers[
            createConfig.authHeaderKey
              ? createConfig.authHeaderKey
              : AuthorizationHeaderKey
          ] = authHeaderValue;
        } else {
          abortController.abort();
        }
      }

      return req;
    });
  }

  if (createConfig.addRefreshTokenInterceptor) {
    axios.interceptors.response.use(
      response => response,
      async error => {
        if (Axios.isAxiosError(error)) {
          const statusCode = error.response?.status;

          if (statusCode === 401) {
            const originalRequest = error.config!;

            if (!originalRequest.withAuth) {
              return Promise.reject(error);
            }

            if (isRefreshing) {
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject, config: originalRequest });
              });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
              const refreshResponse = await axiosForRefresh.post<
                unknown,
                AxiosResponse<GeneralApiResponse<RefreshTokenResponse>>,
                { refreshToken: string }
              >(createConfig.refreshTokenEndpoint, {
                refreshToken: createConfig.getRefreshToken(),
              });

              const newAccessToken =
                refreshResponse.data.data?.accessToken ?? '';
              const newRefreshToken =
                refreshResponse.data.data?.refreshToken ?? '';

              createConfig.onRefreshSuccess({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              });

              if (newAccessToken) {
                processQueue(null, newAccessToken);

                originalRequest.headers[
                  createConfig.authHeaderKey
                    ? createConfig.authHeaderKey
                    : AuthorizationHeaderKey
                ] = newAccessToken;

                return axios.request(originalRequest);
              }
            } catch (refreshError) {
              processQueue(refreshError, null);

              if (Axios.isAxiosError(refreshError)) {
                const refreshStatusCode = refreshError.response?.status;

                if (refreshStatusCode === 401) {
                  onRefreshFail?.();
                }
              }

              return Promise.reject(refreshError);
            } finally {
              isRefreshing = false;
            }
          }
        }

        return Promise.reject(error);
      },
    );
  }

  function doGet<R, D>(requestConfig: ApiRequestConfig<D>) {
    const config = createRequestConfig(requestConfig);
    return axios.get<unknown, R>(requestConfig.endpoint, config);
  }

  function doPost<R, D>(requestConfig: ApiRequestConfig<D>) {
    const config = createRequestConfig(requestConfig);
    return axios.post<unknown, R>(
      requestConfig.endpoint,
      requestConfig.data,
      config,
    );
  }

  function doPut<R, D>(requestConfig: ApiRequestConfig<D>) {
    const config = createRequestConfig(requestConfig);
    return axios.put<unknown, R>(
      requestConfig.endpoint,
      requestConfig.data,
      config,
    );
  }

  function doPatch<R, D>(requestConfig: ApiRequestConfig<D>) {
    const config = createRequestConfig(requestConfig);
    return axios.patch<unknown, R>(
      requestConfig.endpoint,
      requestConfig.data,
      config,
    );
  }

  function doDelete<R, D>(requestConfig: ApiRequestConfig<D>) {
    const config = createRequestConfig(requestConfig);
    return axios.delete<unknown, R>(requestConfig.endpoint, config);
  }

  async function _makeApiRequest<D, R>(
    requestConfig: ApiRequestConfig<D>,
  ): Promise<ApiResult<R>> {
    try {
      let response: AxiosResponse<R>;

      switch (requestConfig.method) {
        case HttpMethod.Get:
          response = await doGet<AxiosResponse<R>, D>(requestConfig);
          break;
        case HttpMethod.Post:
          response = await doPost<AxiosResponse<R>, D>(requestConfig);
          break;
        case HttpMethod.Put:
          response = await doPut<AxiosResponse<R>, D>(requestConfig);
          break;
        case HttpMethod.Patch:
          response = await doPatch<AxiosResponse<R>, D>(requestConfig);
          break;
        case HttpMethod.Delete:
          response = await doDelete<AxiosResponse<R>, D>(requestConfig);
          break;
        default:
          throw new Error('Invalid Http Method');
      }

      return createApiSuccessResult(response.data, response);
    } catch (error) {
      let failureResult: ApiFailureResult;

      if (Axios.isAxiosError(error)) {
        failureResult = createApiFailureResult({ cause: error });
        const statusCode = error.response?.status;

        /**
         * Condition copied from
         * https://github.com/infinitered/apisauce/blob/a9e015a1c6ae649dc521490c41d1054b091f6639/lib/apisauce.ts#L83
         */
        if (error.message === NetworkErrorMessage) {
          failureResult.message = ApiErrorMessage.Network;
          failureResult.code = 0;
        } else if (statusCode) {
          const data = error.response?.data as GeneralApiResponseData;
          failureResult.message =
            data?.message || data?.error || ApiErrorMessage.General;
          failureResult.code = statusCode;
        } else {
          failureResult.message = ApiErrorMessage.UnableToSendRequest;
          failureResult.code = -1;
        }
      } else if (isError(error)) {
        failureResult = failureResult = createApiFailureResult({
          message: '',
          code: -1,
          cause: error,
        });

        if (error.name === AbortErrorName) {
          failureResult.message = AbortReason;
          failureResult.code = -2;
        } else {
          failureResult.message = ApiErrorMessage.UnableToSendRequest;
        }
      } else {
        failureResult = createApiFailureResult({
          message: ApiErrorMessage.UnableToSendRequest,
          code: -1,
        });
      }

      if (requestConfig.rejectOnError ?? createConfig.rejectOnError ?? false) {
        return Promise.reject(failureResult);
      }

      return failureResult;
    }
  }

  const makeApiRequest = <R = GeneralApiResponse, D = undefined>(
    requestConfig: ApiRequestConfig<D>,
  ) => _makeApiRequest<D, R>(requestConfig);

  return {
    makeApiRequest,

    set onRefreshFailHandler(handler: OnRefreshFail) {
      onRefreshFail = handler;
    },
  };
}
